import * as React from 'react';
import type { Id } from '@/convex';
import { useRepoScan } from '@/hooks/useRepoScan';

/**
 * Findings-page rescan button handler.
 *
 * Routes through /api/findings/scan with cacheMode='force' so the user
 * always gets a fresh extraction, and triggeredBy='manual-rescan' for
 * telemetry. The endpoint dedups against any active run for the same
 * project+repo+profile, so double-clicks are safe — the second call
 * resolves to the in-flight runId instead of stacking a new run.
 */
export function useManualRescan({
  orgId,
  projectId,
  repoFullName,
}: {
  orgId: Id<'orgs'> | undefined;
  projectId: Id<'projects'> | undefined;
  repoFullName: string | undefined;
}) {
  const { startScan } = useRepoScan(null);
  return React.useCallback(() => {
    if (!orgId || !projectId || !repoFullName) return;
    void startScan({
      orgId,
      projectId,
      repoFullName,
      cacheMode: 'force',
      scanProfile: 'full',
      triggeredBy: 'manual-rescan',
    });
  }, [orgId, projectId, repoFullName, startScan]);
}
